import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { navigate } from '../../lib/router';
import { useToast } from '../../ui/Toasts';
import { useCreateItem, useItem, useSaveItem, useStudioAccess } from './api';
import { sameJson } from './journey';

/**
 * One config item being edited (tasks 41–43): a copy of the stored body kept in the browser until saved
 * with the revision it came from, or copied into the author's own scope when it is inherited.
 */
export function useItemDraft<T>(id: string, back: (id: string) => string) {
  const access = useStudioAccess();
  const item = useItem(id);
  const save = useSaveItem();
  const create = useCreateItem();
  const toast = useToast();
  const [draft, setDraft] = useState<T | null>(null);
  // A fresh copy whenever another revision loads (adjusted while rendering, not in an effect).
  const stamp = item.data ? `${item.data.id}:${item.data.revision}` : null;
  const [loaded, setLoaded] = useState<string | null>(null);
  if (stamp && stamp !== loaded) {
    setLoaded(stamp);
    setDraft(structuredClone(item.data!.body) as T);
  }
  const owns = !!item.data && access.edits && access.own.includes(item.data.scope_level);
  const dirty = !!item.data && draft !== null && !sameJson(draft, item.data.body);

  return {
    item,
    draft,
    setDraft: (f: (d: T) => T) => setDraft((d) => (d === null ? d : f(structuredClone(d)))),
    owns,
    canCustomise: !!item.data && access.edits && !owns,
    dirty,
    saving: save.isPending || create.isPending,
    save: async () => {
      if (!item.data || draft === null) return;
      try {
        await save.mutateAsync({ id: item.data.id, body: draft, revision: item.data.revision });
        toast('Draft saved. Submit a release to publish it.', 'ok');
      } catch (e) {
        toast(saveError(e), 'bad');
      }
    },
    customise: async () => {
      if (!item.data) return;
      try {
        const row = await create.mutateAsync({ kind: item.data.kind, key: item.data.key, body: item.data.body, note: 'Customised from the platform version' }); // prettier-ignore
        navigate(back(row.id));
        toast(`${item.data.key} is now your own copy`, 'ok');
      } catch (e) {
        toast(e instanceof Error ? e.message : 'Could not copy it', 'bad');
      }
    },
  };
}

export function saveError(e: unknown): string {
  if (e instanceof ApiError && e.status === 409)
    return 'Someone else saved this since you opened it. Reload to see their version (your unsaved edits will be lost).';
  if (e instanceof ApiError && e.issues.length)
    return `Not saved: ${e.issues.map((i) => `${i.path} ${i.message}`).join('; ')}`;
  return e instanceof Error ? e.message : 'Could not save';
}
